import {
  collection,
  getCountFromServer,
  getDocs,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { geohashForLocation } from "geofire-common";
import { db } from "../firebase";

export const LOCAL_HELPER_MOMENTUM_PERIOD_DAYS = 30;
export const LOCAL_HELPER_MOMENTUM_CACHE_MS = 5 * 60 * 1000;
export const LOCAL_HELPER_STARTING_SOON_LOOKBACK_DAYS = 60;

const DAY_MS = 24 * 60 * 60 * 1000;
const applicationCountCache = new Map();

const toFiniteCoordinate = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const getLocalHelperMomentumGeohash5 = ({
  lat = null,
  lng = null,
  geohash5 = "",
} = {}) => {
  const providedGeohash5 = String(geohash5 || "").trim().slice(0, 5);
  if (providedGeohash5.length === 5) return providedGeohash5;

  const latitude = toFiniteCoordinate(lat);
  const longitude = toFiniteCoordinate(lng);
  if (latitude === null || longitude === null) return "";

  return geohashForLocation([latitude, longitude], 5);
};

export const buildLocalHelperApplicationWindows = ({
  now = new Date(),
} = {}) => {
  const currentEnd = now instanceof Date ? now : new Date(now);
  const currentStart = new Date(
    currentEnd.getTime() - LOCAL_HELPER_MOMENTUM_PERIOD_DAYS * DAY_MS,
  );
  const previousStart = new Date(
    currentStart.getTime() - LOCAL_HELPER_MOMENTUM_PERIOD_DAYS * DAY_MS,
  );

  return {
    current: { start: currentStart, end: currentEnd },
    previous: { start: previousStart, end: currentStart },
  };
};

const createApplicationCountQuery = ({ geohash5, start, end }) =>
  query(
    collection(db, "employees"),
    where("geohash5Arr", "array-contains", geohash5),
    where("admin.applicationCompletedAt", ">=", Timestamp.fromDate(start)),
    where("admin.applicationCompletedAt", "<", Timestamp.fromDate(end)),
  );

export const clearLocalHelperMomentumCountCache = () => {
  applicationCountCache.clear();
};

export const getLocalHelperApplicationCounts = async ({
  lat = null,
  lng = null,
  geohash5 = "",
  now = new Date(),
  bypassCache = false,
} = {}) => {
  const resolvedGeohash5 = getLocalHelperMomentumGeohash5({
    lat,
    lng,
    geohash5,
  });
  if (!resolvedGeohash5) {
    return {
      geohash5: "",
      appliedRecently: 0,
      previousPeriodApplied: 0,
      appliedInLastTwoPeriods: 0,
    };
  }

  const nowMs = (now instanceof Date ? now : new Date(now)).getTime();
  const cacheBucket = Math.floor(nowMs / LOCAL_HELPER_MOMENTUM_CACHE_MS);
  const cacheKey = `${resolvedGeohash5}:${cacheBucket}`;
  if (!bypassCache && applicationCountCache.has(cacheKey)) {
    return applicationCountCache.get(cacheKey);
  }

  const loadPromise = (async () => {
    const windows = buildLocalHelperApplicationWindows({ now });
    const [currentSnapshot, previousSnapshot] = await Promise.all([
      getCountFromServer(
        createApplicationCountQuery({
          geohash5: resolvedGeohash5,
          start: windows.current.start,
          end: windows.current.end,
        }),
      ),
      getCountFromServer(
        createApplicationCountQuery({
          geohash5: resolvedGeohash5,
          start: windows.previous.start,
          end: windows.previous.end,
        }),
      ),
    ]);
    const appliedRecently = Number(currentSnapshot.data()?.count || 0);
    const previousPeriodApplied = Number(previousSnapshot.data()?.count || 0);

    return {
      geohash5: resolvedGeohash5,
      appliedRecently,
      previousPeriodApplied,
      appliedInLastTwoPeriods: appliedRecently + previousPeriodApplied,
    };
  })();

  if (!bypassCache) {
    applicationCountCache.set(cacheKey, loadPromise);
    loadPromise.catch(() => applicationCountCache.delete(cacheKey));
  }

  return loadPromise;
};

export const getLocalStartingSoonHelpers = async ({
  lat = null,
  lng = null,
  geohash5 = "",
  now = new Date(),
} = {}) => {
  const resolvedGeohash5 = getLocalHelperMomentumGeohash5({
    lat,
    lng,
    geohash5,
  });
  if (!resolvedGeohash5) return {};

  const currentDate = now instanceof Date ? now : new Date(now);
  const cutoff = new Date(
    currentDate.getTime() -
      LOCAL_HELPER_STARTING_SOON_LOOKBACK_DAYS * DAY_MS,
  );
  const snapshot = await getDocs(
    query(
      collection(db, "employees"),
      where("status", "in", ["onboarding", "finalize"]),
      where("geohash5Arr", "array-contains", resolvedGeohash5),
      where("admin.lastActive", ">=", Timestamp.fromDate(cutoff)),
    ),
  );

  return snapshot.docs
    .filter((employeeDoc) => employeeDoc.data()?.bookingBlocked !== true)
    .map((employeeDoc) => ({
      id: employeeDoc.id,
      ...employeeDoc.data(),
    }))
    .sort((left, right) => {
      const leftMillis = left?.admin?.lastActive?.toMillis?.() || 0;
      const rightMillis = right?.admin?.lastActive?.toMillis?.() || 0;
      return (
        rightMillis - leftMillis ||
        String(left?.id || "").localeCompare(String(right?.id || ""))
      );
    })
    .reduce((helpers, helper) => {
      helpers[helper.id] = helper;
      return helpers;
    }, {});
};
